"use strict";
// Basic performance metrics suite.
Object.defineProperty(exports, "__esModule", { value: true });
exports.NoMetricsContext = exports.MeasureMetricsContext = exports.nullPromise = exports.noParamsLogger = exports.consoleLogger = void 0;
exports.withContext = withContext;
exports.setOperationLogProfiling = setOperationLogProfiling;
exports.registerOperationLog = registerOperationLog;
exports.updateOperationLog = updateOperationLog;
exports.addOperation = addOperation;
const _1 = require(".");
const metrics_1 = require("./metrics");
const errorPrinter = ({ message, stack, ...rest }) => ({
    message,
    stack,
    ...rest
});
function replacer(value) {
    return value instanceof Error ? errorPrinter(value) : value;
}
const consoleLogger = (logParams) => ({
    info: (msg, args) => {
        console.info(msg, ...Object.entries({ ...(args ?? {}), ...(logParams ?? {}) }).map((it) => `${it[0]}=${JSON.stringify(replacer(it[1]))}`));
    },
    error: (msg, args) => {
        console.error(msg, ...Object.entries({ ...(args ?? {}), ...(logParams ?? {}) }).map((it) => `${it[0]}=${JSON.stringify(replacer(it[1]))}`));
    },
    warn: (msg, args) => {
        console.warn(msg, ...Object.entries(args ?? {}).map((it) => `${it[0]}=${JSON.stringify(replacer(it[1]))}`));
    },
    close: async () => { },
    logOperation: (operation, time, params) => { }
});
exports.consoleLogger = consoleLogger;
exports.noParamsLogger = (0, exports.consoleLogger)({});
exports.nullPromise = Promise.resolve();
/**
 * @public
 */
class MeasureMetricsContext {
    parent;
    logParams;
    name;
    params;
    fullParams = {};
    logger;
    metrics;
    id;
    st = (0, _1.platformNow)();
    contextData = {};
    done(value, override) {
        (0, metrics_1.updateMeasure)(this.metrics, this.st, this.params, this.fullParams, (spend) => { }, value, override);
    }
    constructor(name, params, fullParams = {}, metrics = (0, metrics_1.newMetrics)(), logger, parent, logParams) {
        this.parent = parent;
        this.logParams = logParams;
        this.name = name;
        this.params = params;
        this.fullParams = fullParams;
        this.metrics = metrics;
        // Fast path: skip Object.entries alloc when no params. ctx.with('name', {}, ...)
        // is by far the most common call site. namedParams is always pre-allocated.
        for (const k in params) {
            if (!Object.prototype.hasOwnProperty.call(params, k))
                continue;
            const v = params[k];
            if (this.metrics.namedParams[k] !== v) {
                this.metrics.namedParams[k] = v;
            }
            else {
                this.metrics.namedParams[k] = '*';
            }
        }
        this.logger = logger ?? (this.logParams != null ? (0, exports.consoleLogger)(this.logParams ?? {}) : exports.noParamsLogger);
    }
    measure(name, value, labelsOrOverride, override) {
        const isLabels = typeof labelsOrOverride === 'object' && labelsOrOverride !== null;
        const labels = isLabels ? labelsOrOverride : undefined;
        const ov = isLabels ? override : labelsOrOverride;
        const c = new MeasureMetricsContext('#' + name, labels ?? {}, {}, (0, metrics_1.childMetricsSingle)(this.metrics, '#' + name), this.logger, this);
        c.contextData = this.contextData;
        c.done(value, ov);
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let root = this;
        while (root.parent != null)
            root = root.parent;
        const sink = root.externalMetricSink;
        sink?.(name, value, labels);
    }
    recordDuration(name, ms, labels) {
        // In-memory metrics implementation: accumulate via childMetrics
        const c = new MeasureMetricsContext('@' + name, labels ?? {}, {}, (0, metrics_1.childMetricsSingle)(this.metrics, '@' + name), this.logger, this);
        c.contextData = this.contextData;
        c.done(ms);
        // Propagate to external sink (root-level registration). Traverse to root.
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let root = this;
        while (root.parent != null)
            root = root.parent;
        const sink = root.externalMetricSink;
        sink?.(name, ms, labels);
    }
    externalMetricSink;
    newChild(name, params, opt) {
        const result = new MeasureMetricsContext(name, params, opt?.fullParams ?? {}, (0, metrics_1.childMetricsSingle)(this.metrics, name), opt?.logger ?? this.logger, this, this.logParams);
        result.id = this.id;
        result.contextData = this.contextData;
        return result;
    }
    with(name, params, op, fullParams, opt) {
        const c = this.newChild(name, params, { fullParams, logger: this.logger });
        const metric = opt?.metric;
        let needFinally = true;
        try {
            const value = op(c);
            if (value instanceof Promise) {
                needFinally = false;
                return value.finally(() => {
                    const elapsed = (0, _1.platformNowDiff)(c.st);
                    c.end();
                    if (metric !== undefined) {
                        this.recordDuration(metric, elapsed, { op: name, ...params });
                    }
                    if (opt?.log === true) {
                        this.logger.logOperation(name, elapsed, {
                            ...params,
                            ...fullParams
                        });
                    }
                });
            }
            else {
                if (value == null) {
                    return exports.nullPromise;
                }
                return Promise.resolve(value);
            }
        }
        finally {
            if (needFinally) {
                if (metric !== undefined) {
                    this.recordDuration(metric, (0, _1.platformNowDiff)(c.st), { op: name, ...params });
                }
                c.end();
            }
        }
    }
    extractMeta() {
        return {};
    }
    withSync(name, params, op, fullParams) {
        const c = this.newChild(name, params, { fullParams, logger: this.logger });
        try {
            return op(c);
        }
        finally {
            c.end();
        }
    }
    error(message, args) {
        this.logger.error(message, { ...this.params, ...args, ...(this.logParams ?? {}) });
    }
    info(message, args) {
        this.logger.info(message, { ...this.params, ...args, ...(this.logParams ?? {}) });
    }
    warn(message, args) {
        this.logger.warn(message, { ...this.params, ...args, ...(this.logParams ?? {}) });
    }
    end() {
        this.done();
    }
    getParams() {
        return this.params;
    }
}
exports.MeasureMetricsContext = MeasureMetricsContext;
class NoMetricsContext {
    logger;
    id;
    contextData = {};
    constructor(logger) {
        this.logger = logger ?? (0, exports.consoleLogger)({});
    }
    measure(name, value, labelsOrOverride, override) { }
    recordDuration(name, ms, labels) { }
    newChild(name, params, fullParams, logger) {
        const result = new NoMetricsContext(logger ?? this.logger);
        result.id = this.id;
        result.contextData = this.contextData;
        return result;
    }
    with(name, params, op, fullParams) {
        const r = op(this.newChild(name, params, fullParams, this.logger));
        return r instanceof Promise ? r : Promise.resolve(r);
    }
    extractMeta() {
        return {};
    }
    withSync(name, params, op, fullParams) {
        const c = this.newChild(name, params, fullParams, this.logger);
        return op(c);
    }
    withLog(name, params, op, fullParams) {
        const r = op(this.newChild(name, params, fullParams, this.logger));
        return r instanceof Promise ? r : Promise.resolve(r);
    }
    error(message, args) {
        this.logger.error(message, { ...args });
    }
    info(message, args) {
        this.logger.info(message, { ...args });
    }
    warn(message, args) {
        this.logger.warn(message, { ...args });
    }
    end() { }
    getParams() {
        return {};
    }
}
exports.NoMetricsContext = NoMetricsContext;
/**
 * Allow to use decorator for context enabled functions
 */
function withContext(name, params = {}, options) {
    return (target, propertyKey, descriptor) => {
        const originalMethod = descriptor.value;
        descriptor.value = function (...args) {
            const ctx = args[0];
            return ctx.with(name, params, (ctx) => originalMethod.apply(this, [ctx, ...args.slice(1)]), {}, options);
        };
        return descriptor;
    };
}
let operationProfiling = false;
function setOperationLogProfiling(value) {
    operationProfiling = value;
}
let globalId = 0;
function registerOperationLog(ctx) {
    if (!operationProfiling) {
        return {};
    }
    const op = { start: (0, _1.platformNow)(), ops: [], end: -1 };
    let opLogMetrics;
    ctx.id ??= 'op_' + (++globalId).toString(16);
    if (ctx.metrics !== undefined) {
        ctx.metrics.opLog ??= {};
        ctx.metrics.opLog[ctx.id] = op;
        opLogMetrics = ctx.metrics;
    }
    return { opLogMetrics, op };
}
function updateOperationLog(opLogMetrics, op) {
    if (!operationProfiling) {
        return;
    }
    if (op !== undefined) {
        op.end = (0, _1.platformNow)();
    }
    // We should keep only longest one entry
    if (opLogMetrics?.opLog !== undefined) {
        const entries = Object.entries(opLogMetrics.opLog);
        const incomplete = entries.filter((it) => it[1].end === -1);
        const complete = entries.filter((it) => it[1].end !== -1);
        complete.sort((a, b) => a[1].start - b[1].start);
        if (complete.length > 30) {
            complete.splice(0, complete.length - 30);
        }
        opLogMetrics.opLog = Object.fromEntries(incomplete.concat(complete));
    }
}
function addOperation(ctx, name, params, op, fullParams) {
    if (!operationProfiling) {
        return op(ctx);
    }
    let opEntry;
    let p = ctx;
    let opLogMetrics;
    let id;
    while (p !== undefined) {
        if (p.metrics?.opLog !== undefined) {
            opLogMetrics = p.metrics;
        }
        if (id === undefined && p.id !== undefined) {
            id = p.id;
        }
        p = p.parent;
    }
    const opLog = id !== undefined ? opLogMetrics?.opLog?.[id] : undefined;
    if (opLog !== undefined) {
        opEntry = {
            op: name,
            start: performance.now(),
            params: {},
            end: -1
        };
    }
    const result = op(ctx);
    if (opEntry !== undefined && opLog !== undefined) {
        void result.finally(() => {
            if (opEntry !== undefined && opLog !== undefined) {
                opEntry.end = performance.now();
                opEntry.params = { ...params, ...(typeof fullParams === 'function' ? fullParams() : fullParams) };
                opLog.ops.push(opEntry);
            }
        });
    }
    return result;
}
//# sourceMappingURL=context.js.map